import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { BarChart2, ArrowRight, ArrowLeft, Users, Radio } from 'lucide-react';
import '../styles/agentPage.css';
import '../styles/affinityAgent.css';

function AffinityAgent() {
  const { user, isAuthenticated, refreshToken } = useAuth();
  const navigate = useNavigate();
  const [chat, setChat] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [affinityDataSufficient, setAffinityDataSufficient] = useState(false);
  const [currentAudience, setCurrentAudience] = useState(null);
  const chatWindowRef = useRef(null);
  
  const formatMessage = useCallback((content) => {
    return content
    .replace(/^### (.*$)/gm, '<h3>$1</h3>')
    .replace(/^## (.*$)/gm, '<h2>$1</h2>')
    .replace(/^# (.*$)/gm, '<h1>$1</h1>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/- (.*?)(?:\n|$)/g, '<li>$1</li>')
    .replace(/<li>/g, '<ul><li>').replace(/<\/li>\n/g, '</li></ul>')
    .replace(/(\w+)\[(https?:\/\/[^\]]+)\]/g, '<a href="$2" target="_blank">$1</a>')
    .replace(/\n\n/g, '<br/><br/>')
    .replace(/\n/g, '<br/>');
  }, []);

  // Get audience from session storage
  useEffect(() => {
    const storedAudience = sessionStorage.getItem('currentAudience');
    if (storedAudience) {
      setCurrentAudience(JSON.parse(storedAudience));
    } else {
      // If no audience data found, redirect back to audience agent
      navigate('/agent/audience');
    }
  }, [navigate]);

  const getWelcomeMessage = useCallback(async () => {
    if (!currentAudience) return;

    try {
      await refreshToken();
      const result = await axios.post(
        'https://n8n.iniai.cloud/webhook/afi-channel-affinity-agent',
        {
          type: 'welcome',
          user: {
            id: user?.id || '',
            email: user?.email || '',
            role: user?.role || '',
            name: user?.name || ''
          },
          audience: currentAudience,
          sessionId: `${user?.id || 'anonymous'}-${Date.now()}`
        },
        {
          headers: {
            'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY,
            
          }
        }
      );

      if (Array.isArray(result.data) && result.data.length > 0) {
        const response = result.data[0].output || result.data[0].response;
        if (response) {
          setChat([{ 
            type: 'agent', 
            content: formatMessage(response)
          }]);
        }
      }
    } catch (error) {
      console.error('Error fetching welcome message:', error);
      setChat([{
        type: 'agent',
        content: `Welcome to the Channel Affinity Agent. I'll help you determine how well different channels will perform with your "${currentAudience?.name}" audience in the ${currentAudience?.market} market. What would you like to know about channel affinities?`
      }]);
    }
  }, [user, isAuthenticated, refreshToken, formatMessage, currentAudience]);

  useEffect(() => {
    if (isAuthenticated && user && chat.length === 0 && currentAudience) {
      getWelcomeMessage();
    }
  }, [isAuthenticated, user, chat.length, getWelcomeMessage, currentAudience]);

  useEffect(() => {
    if (chatWindowRef.current) {
      requestAnimationFrame(() => {
        chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
      });
    }
  }, [chat]);

  // Check for affinity data confirmation in the agent's response
  useEffect(() => {
    if (chat.length > 1) {
      const lastMessage = chat[chat.length - 1];
      if (lastMessage.type === 'agent') {
        // Look for affinity data confirmation in the message
        const content = lastMessage.content.toLowerCase();
        if (content.includes('affinity data saved') || 
            content.includes('affinity score updated') || 
            content.includes('added to database') ||
            content.includes('affinity details saved')) {
          setAffinityDataSufficient(true);
        }
        
        // Check if we have at least 2 channel affinities to consider the data sufficient
        let affinityCount = 0;
        chat.forEach(msg => {
          if (msg.type === 'agent' && 
             (msg.content.toLowerCase().includes('affinity score') || 
              msg.content.toLowerCase().includes('channel affinity'))) {
            affinityCount++;
          }
        });
        
        if (affinityCount >= 2) {
          setAffinityDataSufficient(true);
        }
      }
    }
  }, [chat]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
  
    const userMessage = { type: 'user', content: query };
    setChat(prevChat => [...prevChat, userMessage]);
    const currentQuery = query;
    setQuery('');
    setLoading(true);
  
    try {
      await refreshToken();
      const result = await axios.post(
        'https://n8n.iniai.cloud/webhook/afi-channel-affinity-agent',
        {
          type: 'query',
          query: currentQuery,
          audience: currentAudience,
          user: {
            id: user?.id || '',
            email: user?.email || '',
            role: user?.role || '',
            name: user?.name || ''
          },
          sessionId: `${user?.id || 'anonymous'}-${Date.now()}`
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY,
          }
        }
      );
  
      if (Array.isArray(result.data) && result.data.length > 0) {
        const formattedContent = formatMessage(result.data[0].response || result.data[0].output);
        setChat(prevChat => [...prevChat, { type: 'agent', content: formattedContent }]);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'An error occurred while processing your request.';
      setChat(prevChat => [...prevChat, { type: 'agent', content: errorMessage }]);
    } finally {
      setLoading(false);
    }
  };

  const handleContinueToPlanner = () => {
    navigate('/reachcurve/planner');
  };

  const handleBackToChannel = () => {
    navigate('/agent/channel');
  };

  const handleBackToAudience = () => {
    navigate('/agent/audience');
  };

  return (
    <div className="agent-container">
      <div className="agent-header">
        <div className="agent-title">
          <BarChart2 className="agent-icon" />
          <h1>Afi: Channel Affinity Agent</h1>
        </div>
        <p className="agent-description">
          Afi helps you determine which channels have the strongest affinity with your target audience.
        </p>
      </div>

      <div className="audience-reference">
        <h3>Working with audience:</h3>
        <div className="audience-badge">
          <span className="audience-name">{currentAudience?.name}</span>
          <span className="audience-size">{currentAudience?.size}</span>
          <span className="audience-market">{currentAudience?.market}</span>
        </div>
      </div>

      {affinityDataSufficient && (
        <div className="affinity-data-sufficient">
          <div className="sufficient-notice">
            <BarChart2 className="check-icon" />
            <h3>Affinity Data Collected</h3>
            <p>You have gathered enough channel affinity data for your campaign.</p>
          </div>
          <div className="next-steps">
            <h4>What would you like to do next?</h4>
            <div className="option-buttons">
              <button className="option-button primary" onClick={handleContinueToPlanner}>
                <Radio className="option-icon" />
                <span>Continue to Reach Curve</span>
                <ArrowRight className="arrow-icon" />
              </button>
              <div className="secondary-options">
                <button className="option-button back" onClick={handleBackToChannel}>
                  <ArrowLeft className="option-icon" />
                  <span>Back to Channel Pricing</span>
                </button>
                <button className="option-button back" onClick={handleBackToAudience}>
                  <ArrowLeft className="option-icon" />
                  <span>Back to Audience</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="chat-window" ref={chatWindowRef}>
        {chat.map((message, index) => (
          <div
            key={index}
            className={`message-bubble ${message.type === 'user' ? 'user' : 'agent'}`}
          >
            <div 
              dangerouslySetInnerHTML={{ __html: message.content }} 
              className="message-content"
            />
          </div>
        ))}

        {loading && (
          <div className="message-bubble agent">
            <p className="loading-text">
              Afi is analyzing channel affinities
              <span className="dots">...</span>
            </p>
          </div>
        )}
      </div>

      <form className="chat-input" onSubmit={handleSubmit}>
        <textarea
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Ask about channel affinities (e.g., How well does my audience engage with social media vs. display ads?)..."
          rows="3"
        />
        <button type="submit" disabled={loading}>
          {loading ? 'Processing...' : 'Send'}
        </button>
      </form>
    </div>
  );
}

export default AffinityAgent;